import { api } from './api'
import { getAll } from './pagination'

export interface ParentDashboardData {
  children: Array<{
    id: number
    student_id: string
    name: string
    class: string
    class_teacher: string
    photo_url?: string
    attendance_percentage: number
    recent_grade?: string
    outstanding_fees: number
    next_payment_due?: string
    next_payment_invoice_id?: number
  }>
  notifications: Array<{
    id: number
    type: 'attendance' | 'academic' | 'payment' | 'general'
    title: string
    message: string
    date: string
    is_read: boolean
    priority: 'low' | 'medium' | 'high' | 'critical'
  }>
  upcoming_events: Array<{
    id: number
    title: string
    date: string
    type: 'exam' | 'meeting' | 'event' | 'holiday'
    description?: string
  }>
  recent_communications: Array<{
    id: number
    from: string
    subject: string
    date: string
    is_read: boolean
    type: 'message' | 'announcement' | 'report'
  }>
  statistics: {
    total_children: number
    total_outstanding_fees: number
    average_attendance: number
    unread_notifications: number
    unread_messages: number
  }
}

export interface StudentDetail {
  id: number
  student_id: string
  name: string
  class: string
  class_teacher: string
  photo_url?: string
  attendance: {
    current_month: number
    last_month: number
    total_days_present: number
    total_days_absent: number
    recent_attendance: Array<{
      date: string
      status: 'present' | 'absent' | 'late' | 'excused'
      period: string
    }>
  }
  academics: {
    current_subjects: Array<{
      name: string
      teacher: string
      recent_grade?: string
      assignments_due: number
    }>
    recent_grades: Array<{
      subject: string
      grade: string
      date: string
      type: 'assignment' | 'exam' | 'quiz'
    }>
    upcoming_exams: Array<{
      subject: string
      date: string
      time: string
      location?: string
    }>
  }
  payments: {
    outstanding_amount: number
    recent_payments: Array<{
      id: number
      amount: number
      date: string
      purpose: string
      status: 'completed' | 'pending' | 'failed'
    }>
    upcoming_due_dates: Array<{
      invoice_id: number
      amount: number
      due_date: string
      purpose: string
      allows_partial_payment: boolean
      minimum_payment?: number
    }>
  }
  health: {
    medical_conditions?: string
    allergies?: string
    medications?: string
    doctor_name?: string
    doctor_phone?: string
    recent_visits: Array<{
      date: string
      reason: string
      notes?: string
    }>
  }
  transport: {
    uses_transport: boolean
    route?: string
    pickup_location?: string
    pickup_time?: string
    dropoff_time?: string
  }
}

export interface CommunicationMessage {
  id: number
  sender: number
  sender_name: string
  recipient: number
  recipient_name: string
  subject: string
  content: string
  /** Delivery channel -- email, sms, push, in_app. Not a category. */
  message_type: string
  status: string
  is_read: boolean
  created_at: string
  read_at?: string | null
  reply_to?: number | null
}

export interface ParentNotification {
  id: number
  type: 'attendance' | 'academic' | 'payment' | 'health' | 'transport' | 'general'
  title: string
  message: string
  date: string
  is_read: boolean
  priority: 'low' | 'medium' | 'high' | 'critical'
  student_id?: number
  student_name?: string
  action_required: boolean
  action_url?: string
}

export interface ParentReport {
  id: number
  title: string
  type: 'attendance' | 'academic' | 'behavior' | 'health' | 'financial'
  student_id: number
  student_name: string
  period: string
  generated_date: string
  file_url: string
  file_size: number
  is_downloaded: boolean
}

class ParentService {
  // Dashboard data
  async getDashboardData(): Promise<ParentDashboardData> {
    const response = await api.get('/parents/dashboard/')
    return response.data
  }

  // Student details
  async getStudentDetail(studentId: number): Promise<StudentDetail> {
    const response = await api.get(`/parents/students/${studentId}/`)
    return response.data
  }

  // Communications
  async getCommunications(params?: {
    page?: number
    limit?: number
    is_read?: boolean
  }) {
    return getAll<CommunicationMessage>('/communication/parents/messages/', params)
  }

  async getCommunication(id: number): Promise<CommunicationMessage> {
    const response = await api.get(`/communication/parents/messages/${id}/`)
    return response.data
  }

  async sendMessage(data: {
    recipient: number
    subject: string
    content: string
    reply_to?: number
  }) {
    // message_type is required by the model and has no default; a message
    // written in the portal is an in-app one.
    const response = await api.post('/communication/parents/messages/', {
      ...data,
      message_type: 'in_app',
    })
    return response.data
  }

  async markCommunicationAsRead(id: number) {
    // The endpoint built for this stamps read_at as well as the flag; a plain
    // PATCH of is_read would have left the portal unable to say when a message
    // was read, and only worked at all by accident of the serializer.
    const response = await api.post(`/communication/parents/messages/${id}/mark-read/`, {})
    return response.data
  }

  // Notifications
  async getNotifications(params?: {
    page?: number
    limit?: number
    type?: string
    priority?: string
    is_read?: boolean
  }) {
    return getAll<ParentNotification>('/parents/notifications/', params)
  }

  async markNotificationAsRead(id: number) {
    const response = await api.patch(`/parents/notifications/${id}/`, {
      is_read: true
    })
    return response.data
  }

  async markAllNotificationsAsRead() {
    const response = await api.post('/parents/notifications/mark-all-read/')
    return response.data
  }

  // Reports
  async getReports(params?: {
    page?: number
    limit?: number
    type?: string
    student_id?: number
  }) {
    return getAll<ParentReport>('/parents/reports/', params)
  }

  async downloadReport(reportId: number) {
    const response = await api.get(`/parents/reports/${reportId}/download/`, {
      responseType: 'blob'
    })
    return response.data
  }

  // Attendance
  async getStudentAttendance(studentId: number, params?: {
    start_date?: string
    end_date?: string
    period?: string
  }) {
    return getAll<any>(`/parents/students/${studentId}/attendance/`, params)
  }

  async getAttendanceAnalytics(studentId: number, days: number = 30) {
    const response = await api.get(`/parents/students/${studentId}/attendance/analytics/`, {
      params: { days }
    })
    return response.data
  }

  // Academics
  async getStudentGrades(studentId: number, params?: {
    subject?: string
    term?: string
    year?: string
  }) {
    return getAll<any>(`/parents/students/${studentId}/grades/`, params)
  }

  async getStudentAssignments(studentId: number, params?: {
    status?: 'pending' | 'submitted' | 'graded'
    subject?: string
  }) {
    return getAll<any>(`/parents/students/${studentId}/assignments/`, params)
  }

  async getStudentExams(studentId: number, params?: {
    status?: 'upcoming' | 'completed'
    subject?: string
  }) {
    return getAll<any>(`/parents/students/${studentId}/exams/`, params)
  }

  // Payments
  async getStudentPayments(studentId: number, params?: {
    status?: 'completed' | 'pending' | 'failed'
    purpose?: string
  }) {
    return getAll<any>(`/parents/students/${studentId}/payments/`, params)
  }

  async getPaymentHistory(studentId: number) {
    const response = await api.get(`/parents/students/${studentId}/payment-history/`)
    return response.data
  }

  // Health
  async getStudentHealth(studentId: number) {
    const response = await api.get(`/parents/students/${studentId}/health/`)
    return response.data
  }

  async updateHealthInfo(studentId: number, data: {
    medical_conditions?: string
    allergies?: string
    medications?: string
    doctor_name?: string
    doctor_phone?: string
  }) {
    const response = await api.patch(`/parents/students/${studentId}/health/`, data)
    return response.data
  }

  // Transport
  async getStudentTransport(studentId: number) {
    const response = await api.get(`/parents/students/${studentId}/transport/`)
    return response.data
  }

  async updateTransportInfo(studentId: number, data: {
    uses_transport: boolean
    route?: string
    pickup_location?: string
    pickup_time?: string
    dropoff_time?: string
  }) {
    const response = await api.patch(`/parents/students/${studentId}/transport/`, data)
    return response.data
  }

  // Events and Calendar
  async getUpcomingEvents(params?: {
    start_date?: string
    end_date?: string
    type?: string
  }) {
    return getAll<any>('/parents/events/', params)
  }

  async getSchoolCalendar() {
    const response = await api.get('/parents/calendar/')
    return response.data
  }

  // Settings and Preferences
  async getPreferences() {
    const response = await api.get('/parents/preferences/')
    return response.data
  }

  async updatePreferences(data: {
    notification_preferences: {
      attendance_alerts: boolean
      academic_updates: boolean
      payment_reminders: boolean
      health_alerts: boolean
      transport_updates: boolean
    }
    communication_preferences: {
      email_notifications: boolean
      sms_notifications: boolean
      push_notifications: boolean
    }
    privacy_settings: {
      share_contact_info: boolean
      allow_photo_sharing: boolean
    }
  }) {
    const response = await api.patch('/parents/preferences/', data)
    return response.data
  }

  // Emergency Contacts
  async getEmergencyContacts() {
    const response = await api.get('/parents/emergency-contacts/')
    return response.data
  }

  async updateEmergencyContacts(data: {
    primary_contact: {
      name: string
      relationship: string
      phone: string
      email?: string
    }
    secondary_contacts: Array<{
      name: string
      relationship: string
      phone: string
      email?: string
    }>
  }) {
    const response = await api.patch('/parents/emergency-contacts/', data)
    return response.data
  }

  // Get teachers for parent's children
  async getTeachersForParent(): Promise<Array<{
    id: number
    name: string
    email: string
    phone: string
    subject: string
    class_name: string
  }>> {
    const response = await api.get('/parents/teachers/')
    return response.data.teachers || []
  }

  // Get communication statistics
  async getCommunicationStats(): Promise<{
    total_messages: number
    unread_messages: number
    total_announcements: number
    response_rate: number
  }> {
    const response = await api.get('/parents/communication-stats/')
    return response.data
  }
}

export const parentService = new ParentService()
